import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CardSelectComponent,
	DividerComponent,
	FormGroupComponent,
	InputComponent,
	ISelect,
	ITagImage,
	IToogleSelect,
	ModalComponent,
	SelectComponent,
	SwitchComponent,
} from '@tecalisdev/infinity';
import { MasterService } from '../../../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../../../shared/services/reactive-forms.service';
import { DesignHelperService } from '../../services/design-helper.service';

export interface IThemeMode {
	title?: string;
	modeAvailable?: ISelect | Array<ISelect> | Array<IToogleSelect> | any;
	company_uuid?: string;
	/** En edición: si true y cambia el skin, se aplica paleta DS. En crear se fuerza true al cambiar modo (sin UI). */
	applyDefaultPaletteOnSkinChange?: boolean;
}

@Component({
	selector: 'app-modal-design-theme',
	imports: [
		ModalComponent,
		ReactiveFormsModule,
		FormGroupComponent,
		InputComponent,
		TranslatePipe,
		CardSelectComponent,
		DividerComponent,
		SelectComponent,
		SwitchComponent,
	],
	templateUrl: './modal-design-theme.component.html',
	styleUrl: './modal-design-theme.component.scss',
})
export class ModalDesignThemeComponent implements OnInit {
	@Input() data?: IThemeMode;
	/** Si true (detalle/edición), se muestra el switch para elegir paleta al cambiar modo. En crear, no se muestra y se fuerza reset de paleta al cambiar modo. */
	@Input() isEditMode = false;
	@Output() closeEvent = new EventEmitter<IThemeMode | null>();

	public designHelper = inject(DesignHelperService);
	public reactiveFormService = inject(ReactiveFormsService);
	public masterService = inject(MasterService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	public form!: FormGroup;
	public title: string = '';
	public buttonPrimaryLabel: string = '';
	public buttonSecondaryLabel: string = '';
	public readonly modalTagImage: ITagImage = { icon: 'icon-customize' };

	/**
	 * Referencia estable para `tec-select` con búsqueda.
	 * `masterService.companies` hace `.map()` en cada acceso y resetea el filtro en CD.
	 */
	public readonly companyOptions: ISelect[] = this.masterService.companies ?? [];

	/** Muestra el switch solo si el modo seleccionado difiere del skin al abrir el modal. */
	public showApplyPaletteOption = signal(false);

	private initialTargetSkin: 'light' | 'dark' = 'light';

	ngOnInit(): void {
		this.initForm();
		this.setLabels();
		this.initialTargetSkin = this.resolveTargetSkin(this.form.get('modeAvailable')?.value);
		this.syncApplyPaletteVisibility();

		this.form
			.get('modeAvailable')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.syncApplyPaletteVisibility();
			});
	}

	private initForm(): void {
		const companies = this.companyOptions;
		const companyControl = new FormControl(this.resolveCompanyValue(companies));

		if (companies.length > 1) {
			companyControl.setValidators(Validators.required);
		}

		const defaultMode =
			this.data?.modeAvailable ?? this.designHelper.availableModes.find((mode) => mode.id === 'light');

		this.form = new FormGroup({
			title: new FormControl(this.data?.title || '', Validators.required),
			modeAvailable: new FormControl(defaultMode, Validators.required),
			company_uuid: companyControl,
			applyDefaultPaletteOnSkinChange: new FormControl(false, { nonNullable: true }),
		});

		companyControl.updateValueAndValidity({ emitEvent: false });
	}

	private resolveCompanyValue(companies: ISelect[]): ISelect | string {
		if (companies.length === 1) {
			return companies[0];
		}

		if (!this.data?.company_uuid) {
			return '';
		}

		return companies.find((company) => company.id === this.data?.company_uuid) || this.data.company_uuid;
	}

	private setLabels(): void {
		const isEdit = Object.keys(this.data || {}).length > 0;
		this.title = isEdit
			? this.trans.instant('DESIGN.MODAL_THEME.EDIT_TITLE')
			: this.trans.instant('DESIGN.MODAL_THEME.CREATE_TITLE');
		this.buttonPrimaryLabel = isEdit
			? this.trans.instant('BUTTON.EDIT')
			: this.trans.instant('BUTTON.CONTINUE');
		this.buttonSecondaryLabel = this.trans.instant('BUTTON.CANCEL');
	}

	public isModeSelected(modeId: 'light' | 'dark'): boolean {
		const value = this.form?.get('modeAvailable')?.value;
		if (value == null || value === '') return false;
		return this.resolveTargetSkin(value) === modeId;
	}

	public onModeCardChange(modeId: 'light' | 'dark', selected: boolean): void {
		const control = this.form.get('modeAvailable');
		if (!selected) {
			// Deseleccionar limpia el valor; required bloquea Continuar
			if (this.isModeSelected(modeId)) {
				control?.setValue(null);
				control?.markAsTouched();
			}
			return;
		}
		this.selectMode(modeId);
	}

	private selectMode(modeId: 'light' | 'dark'): void {
		const mode = this.designHelper.availableModes.find((item) => item.id === modeId);
		if (mode) {
			this.form.get('modeAvailable')?.setValue(mode);
			this.form.get('modeAvailable')?.markAsTouched();
		}
	}

	private resolveTargetSkin(modeAvailable: unknown): 'light' | 'dark' {
		if (modeAvailable == null) {
			return 'light';
		}
		if (typeof modeAvailable === 'boolean') {
			return modeAvailable ? 'light' : 'dark';
		}
		if (typeof modeAvailable === 'object' && modeAvailable !== null && 'value' in modeAvailable) {
			return (modeAvailable as { value: boolean }).value ? 'light' : 'dark';
		}
		if (typeof modeAvailable === 'object' && modeAvailable !== null && 'id' in modeAvailable) {
			return (modeAvailable as { id: string }).id === 'light' ? 'light' : 'dark';
		}
		return 'light';
	}

	private syncApplyPaletteVisibility(): void {
		const current = this.resolveTargetSkin(this.form.get('modeAvailable')?.value);
		const changed = current !== this.initialTargetSkin;
		this.showApplyPaletteOption.set(changed);
		if (!changed) {
			this.form.get('applyDefaultPaletteOnSkinChange')?.setValue(false, { emitEvent: false });
		}
	}

	public closeModal(action: string): void {
		if (action === 'buttonPrimary') {
			if (this.form.invalid) {
				this.form.markAllAsTouched();
				return;
			}
			const company_uuid = this.form?.value?.company_uuid?.id ?? this.form?.value?.company_uuid;
			const result: IThemeMode = { ...this.data, ...this.form.value, company_uuid };
			result.title = result.title?.trim();
			if (!this.showApplyPaletteOption()) {
				result.applyDefaultPaletteOnSkinChange = false;
			} else if (!this.isEditMode) {
				// Crear: siempre aplicar paleta por defecto del modo al cambiar light/dark
				result.applyDefaultPaletteOnSkinChange = true;
			}
			// Editar: conservar el valor del switch (result ya incluye form.value)
			this.closeEvent.emit(result);
		} else {
			this.closeEvent.emit(null);
		}
	}
}
